import { each, forIn, get, includes, mapValues, uniqBy } from "lodash";
import validate from "validate.js";
import listingValidationNonRequired from "../../listing/ducks/listingValidationNonRequired";
import listingValidation from "../../listing/ducks/validation";
import { constraints } from "../../utils/validation";

const validateNonRequired = (comp, useType, transactionType, compType) => {
    let whatToValidate = [];
    switch (transactionType) {
        case "Lease":
            whatToValidate = [
                useType,
                transactionType,
                "leaseDetails",
                "totalConsideration",
                "listingInformation",
                "buildingInformation",
                "parkingInformation",
                "listingFinancial",
            ];
            break;
        case "Sale":
            whatToValidate = [
                useType,
                transactionType,
                "listingInformation",
                "buildingInformation",
                "parkingInformation",
                "saleFinancial",
            ];
            break;
        default:
            break;
    }
    let combinedConstraints = {};
    each(whatToValidate, (section) => {
        combinedConstraints = { ...combinedConstraints, ...outsideOrAppraisalCompConstraintsNotRequired[section] };
    });

    let errors = {
        ...mapValues(validate(comp, combinedConstraints, { fullMessages: false }), (error) => {
            return error[0];
        }),
        ...listingValidation.validateListingBrokersCompany(get(comp, "listing.listingBrokers")),
        ...listingValidationNonRequired.validateMixedUses(get(comp, "listing.attributes.mixedUses")),
        ...listingValidationNonRequired.validatePortfolioProperties(
            get(comp, "listing.attributes.portfolioProperties")
        ),
        ...listingValidationNonRequired.validateCondos(get(comp, "listing.attributes.condos")),
    };

    console.log("Form Errors: ", errors);
    return errors;
};

const getPanelsToExpand = (errors, useType, transactionType) => {
    let listingInformationConstraints = {};
    each([useType, "listingInformation", "parkingInformation"], (section) => {
        listingInformationConstraints = {
            ...listingInformationConstraints,
            ...outsideOrAppraisalCompConstraintsNotRequired[section],
        };
    });

    let generalInformationConstraints = {};
    each([transactionType], (section) => {
        generalInformationConstraints = {
            ...generalInformationConstraints,
            ...outsideOrAppraisalCompConstraintsNotRequired[section],
        };
    });

    const otherListingInfo = [
        "attributes.condos",
        "attributes.mixedUses",
        "attributes.portfolioProperties",
        "listing.listingBrokers",
    ];
    const otherCompInfo = ["installments"];

    let panelsToExpand = [];
    forIn(errors, (value, key) => {
        if (
            includes(
                [
                    ...Object.keys(outsideOrAppraisalCompConstraintsNotRequired.listingFinancial),
                    ...Object.keys(outsideOrAppraisalCompConstraintsNotRequired.saleFinancial),
                ],
                key
            )
        ) {
            panelsToExpand.push("listingFinancial");
        } else if (includes([...Object.keys(listingInformationConstraints), ...otherListingInfo], key)) {
            panelsToExpand.push("listingInformation");
        } else if (includes([...Object.keys(generalInformationConstraints), ...otherCompInfo], key)) {
            //ignore, comp information opened and closed manually
            panelsToExpand.push("compInformation");
        }
    });

    panelsToExpand = uniqBy(panelsToExpand, function (e) {
        return e;
    });

    return panelsToExpand;
};

export const outsideOrAppraisalCompConstraintsNotRequired = {
    Office: {
        "listing.attributes.specialtyType": {},
    },
    Industrial: {
        "listing.attributes.specialtyType": {},
        "listing.attributes.sfAvail": {
            ...constraints.number,
        },
        "listing.attributes.largestContigSf": {
            ...constraints.number,
        },
        "listing.attributes.officeSf": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.yardSf": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.coldStorageSf": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.suite": {},
        "listing.attributes.officeBreakdownSfRate": {
            ...constraints.number,
        },
        "listing.attributes.warehouseShellBreakdownRate": {
            ...constraints.number,
        },
        "listing.attributes.blendedRate": {
            ...constraints.number,
        },
        "listing.attributes.voltsNum": {},
        "listing.attributes.ampsNum": {
            ...constraints.number,
        },
        "listing.attributes.phaseNum": {
            ...constraints.number,
        },
        "listing.attributes.gradeLevelDoorsNum": {
            ...constraints.integer,
        },
        "listing.attributes.dockHighDoorsNum": {
            ...constraints.integer,
        },
        "listing.attributes.railDoorsNum": {
            ...constraints.integer,
        },
        "listing.attributes.clearHeightMin": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.clearHeightMax": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.cranesQty": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.craneNotes": {},
        "listing.attributes.yardNotes": {},
        "listing.attributes.industrialSf": {
            ...constraints.wholeNumber,
        },
    },
    Retail: {
        "listing.attributes.specialtyType": {},
    },
    Land: {
        "listing.attributes.specialtyType": {},
    },
    Multifamily: {
        "listing.attributes.specialtyType": {},
    },
    Hospitality: {
        "listing.attributes.specialtyType": {},
        "listing.attributes.hospitalitySf": {
            ...constraints.id,
        },
        "listing.attributes.hospitalityUnitCount": {
            ...constraints.id,
        },
        "listing.attributes.roomCount": {
            ...constraints.id,
        },
    },
    Other: {
        "listing.attributes.specialtyType": {},
    },
    Educational: {
        "listing.attributes.classroomCount": {
            ...constraints.number,
        },
        "listing.attributes.auditoriumSeatsCount": {
            ...constraints.number,
        },
    },
    Healthcare: {
        "listing.attributes.specialtyType": {},
    },
    totalConsideration: {
        commencementDate: {
            ...constraints.date,
        },
        expirationDate: {
            ...constraints.date,
        },
        termNumMonths: {
            ...constraints.number,
        },
        squareFeet: {
            ...constraints.number,
        },
        ratePerMonth: {
            ...constraints.number,
        },
        ratePerSqftPerMonth: {
            ...constraints.number,
        },
        ratePerSqftPerYear: {
            ...constraints.number,
        },
        escalationRate: {
            ...constraints.number,
        },
        escalationTerm: {
            ...constraints.number,
        },
    },
    listingInformation: {
        "listing.listingName": {
            ...constraints.required,
        },
        "listing.dateListed": {
            ...constraints.date,
        },
        "listing.dateAvailable": {
            ...constraints.date,
        },
        "listing.expirationDate": {
            ...constraints.date,
        },
        "listing.leaseExpirationDate": {
            ...constraints.date,
        },
        "listing.attributes.totalUnits": {
            ...constraints.number,
        },
        "listing.attributes.numBldg": {
            ...constraints.number,
        },
        "listing.attributes.lotAcres": {
            ...constraints.number,
        },
        "listing.attributes.lotSf": {
            ...constraints.number,
        },
        "listing.attributes.amenities": {},
        "listing.attributes.tourInstructions": {},
        "listing.sfAvail": {
            ...constraints.number,
        },
        "listing.sfDivisibleTo": {
            ...constraints.number,
        },
    },
    leaseDetails: {
        tiRate: {
            ...constraints.number,
            ...constraints.between0And1000,
        },
        leaseType: {},
        movingAllowanceAmount: {
            ...constraints.number,
        },
        buyerTenantBusinessType: {},
        naicsCode: {
            ...constraints.id,
        },
        sicCode: {
            ...constraints.id,
        },
        previousLocation: {},
        currentSf: {
            ...constraints.number,
        },
        squareFeetChange: {
            ...constraints.number,
        },
    },
    Lease: {
        //comp information
        transactionDate: {
            ...constraints.date,
        },
        paymentMethod: {},
        leaseBackFlg: {},
        spaceType: {},
        representationType: {},
        //other constraints
        "buyerTenantCompany.displayName": {},
        "buyerTenantCompany.legalName": {},
        "buyerTenantCompany.parentCompany.displayName": {},
        outsideBrokerCommission: {
            ...constraints.number,
        },
    },
    Sale: {
        //required fields
        transactionDate: {
            ...constraints.date,
        },
        totalKmCommission: {},
        paymentMethod: {},
        leaseBackFlg: {},
        spaceType: {},
        representationType: {},
        salePrice: {},
        squareFeet: {
            ...constraints.number,
        },
    },
    parkingInformation: {
        "listing.attributes.parkingTotal": {
            ...constraints.number,
        },
        "listing.attributes.parkingRatio": {},
        "listing.attributes.surfaceParkingSpaceCount": {
            ...constraints.number,
        },
        "listing.attributes.parkingGarageSpaceCount": {
            ...constraints.number,
        },
        "listing.attributes.parkingReservedSpaceCount": {
            ...constraints.number,
        },
        "listing.attributes.carportParkingSpaceCount": {
            ...constraints.number,
        },
        "listing.attributes.parkingNotes": {},
    },
    listingFinancial: {
        "listing.leaseType": {},
        "listing.improvementAllowance": {
            ...constraints.number,
        },
        "listing.askingRentMax": {
            ...constraints.number,
        },
        "listing.askingRentPerMonth": {
            ...constraints.number,
        },
        "listing.baseRentPerYear": {
            ...constraints.number,
        },
        "listing.expenseType": {},
        "listing.expenseTypeValue": {},
        "listing.nnnPerMonth": {
            ...constraints.number,
        },
        "listing.nnnPerYear": {
            ...constraints.number,
        },
        "listing.totalMonthlyRent": {
            ...constraints.number,
        },
        "listing.totalRentPerYear": {
            ...constraints.number,
        },
        "listing.procuringBrokerCommission": {},
    },
    saleFinancial: {
        "listing.listPrice": {},
    },
};

export default {
    getPanelsToExpand,
    validateNonRequired,
};
